import React, {lazy, Suspense, useEffect, useRef, useState} from 'react';
import {useHookstate} from '@hookstate/core';
import classNames from 'classnames';
import {Route, Routes} from 'react-router-dom';
import {CSSTransition} from 'react-transition-group';
import {globalBlockedState, ToastContext, UserContext} from './app-context';

import {AppTopbar} from './AppTopbar';
import {AppMenu} from './AppMenu';
import CookieConsent from "react-cookie-consent";

import {useAuthenticator} from '@aws-amplify/ui-react';
import { addLocale } from 'primereact/api';

import '@aws-amplify/ui-react/styles.css';
import 'primereact/resources/primereact.css';
import "primereact/resources/themes/bootstrap4-light-blue/theme.css";
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './layout/layout.scss';
import './App.scss';
import Home from "./components/Home";
import { I18n } from "aws-amplify/utils";
import {refreshAuthenticationData} from "./service/UserService";
import {BlockUI} from "primereact/blockui";
import {Toast} from "primereact/toast";
import {CustomAuthenticator} from "./CustomAuthenticator";
import { primeln } from "./localization";
import ScrollToTop from "./ScrollToTop";

// Code-split everything but the landing page: each of these (and whatever it pulls in - FullCalendar
// for the schedule pages, the iban lib for declaration de frais, ...) only downloads when a user
// actually navigates there, instead of every one of them loading up front in the main bundle.
const Profile = lazy(() => import("./components/Profile").then(m => ({default: m.Profile})));
const CandidateSchedule = lazy(() => import("./components/schedule/CandidateSchedule").then(m => ({default: m.CandidateSchedule})));
const FormateurSchedule = lazy(() => import("./components/schedule/FormateurSchedule").then(m => ({default: m.FormateurSchedule})));
const FormateurDeclarationsFrais = lazy(() => import("./components/declarationfrais/FormateurDeclarationsFrais").then(m => ({default: m.FormateurDeclarationsFrais})));
const CandidateHistory = lazy(() => import("./components/history/CandidateHistory").then(m => ({default: m.CandidateHistory})));
const Inscriptions = lazy(() => import("./components/inscription/Inscriptions").then(m => ({default: m.Inscriptions})));
const SaisiePresence = lazy(() => import("./components/presence/SaisiePresence").then(m => ({default: m.SaisiePresence})));
const CandidateExcuses = lazy(() => import("./components/excuses/CandidateExcuses").then(m => ({default: m.CandidateExcuses})));
const Accessibilite = lazy(() => import("./components/Accessibilite").then(m => ({default: m.Accessibilite})));

const App = () => {
    const blockedState = useHookstate(globalBlockedState);
    const [userAttributes, setUserAttributes] = useState({});
    const [layoutMode] = useState('static');
    const [layoutColorMode] = useState('light')
    const [inputStyle] = useState('outlined');
    const [ripple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const toast = useRef(null);

    const { authStatus, user } = useAuthenticator((context) => [
        context.user,
        context.authStatus
    ]);

    // PrimeReact.ripple = true;

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        // No forced document.documentElement.style.fontSize here: the Prime theme is
        // rem-based, so leaving the root font-size alone lets browser zoom/text-resizing
        // preferences apply (WCAG 1.4.4).
        addLocale('fr', primeln.fr);
        addLocale('de', primeln.de);
    }, [])

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOverlayMenuActive(false);
                setMobileMenuActive(false);
                setMobileTopbarMenuActive(false);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [])

    useEffect(() => {
        if (authStatus === 'authenticated' && user) {
            refreshAuthenticationData(setUserAttributes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStatus, user]);

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, "body-overflow-hidden");
        } else {
            removeClass(document.body, "body-overflow-hidden");
        }
    }, [mobileMenuActive]);

    // On mobile the sidebar becomes a modal-like overlay covering the page (see the
    // layout-mobile-sidebar-active class below), so opening/closing it is a change of context that
    // needs the same focus handling a dialog would get (WCAG 2.4.3): move focus in on open, and
    // back to the button that opened it on close. Skipped on the very first render so mount doesn't
    // steal focus onto the hamburger button before the user has done anything.
    const mobileMenuMountedRef = useRef(false);
    useEffect(() => {
        if (!mobileMenuMountedRef.current) {
            mobileMenuMountedRef.current = true;
            return;
        }
        if (mobileMenuActive) {
            document.querySelector('.layout-sidebar a, .layout-sidebar button')?.focus();
        } else {
            document.querySelector('.layout-menu-button')?.focus();
        }
    }, [mobileMenuActive]);

    const onWrapperClick = (event) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    }

    const onToggleMenuClick = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if (mobileMenuActive === true) {
                    setOverlayMenuActive(true);
                }

                setOverlayMenuActive((prevState) => !prevState);
                setMobileMenuActive(false);
            } else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        } else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    }

    const onSidebarClick = () => {
        menuClick = true;
    }

    const onMobileTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    }

    const onMobileSubTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        event.preventDefault();
    }

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    }
    const isDesktop = () => {
        return window.innerWidth >= 992;
    }

    const addClass = (element, className) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    const removeClass = (element, className) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    return (<CustomAuthenticator content={({ user }) => (
            (authStatus === 'authenticated' || authStatus === 'signedIn') && user ? (
                <ToastContext.Provider value={toast}>
                    <Toast ref={toast} className="cdm-toast"/>
                    {/* Click-outside-to-close dismissal for the menus, not a control of its own: it has
                        no action a keyboard/AT user could need to reach here that isn't already reachable
                        (Escape and the toggle buttons above already close these same menus). */}
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <div className={wrapperClass} onClick={onWrapperClick}>
                        <a href="#main-content" className="skip-link">{I18n.get("skiplink.maincontent")}</a>
                        <UserContext.Provider value={userAttributes}>
                            <AppTopbar onToggleMenuClick={onToggleMenuClick} layoutColorMode={layoutColorMode}
                                       staticMenuInactive={staticMenuInactive} mobileMenuActive={mobileMenuActive}
                                       mobileTopbarMenuActive={mobileTopbarMenuActive} onMobileTopbarMenuClick={onMobileTopbarMenuClick} onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick}/>

                            {/* onClick here only flags "this click was inside the sidebar" for onWrapperClick
                                above - real navigation/keyboard interaction happens on the links inside
                                AppMenu, which are already fully keyboard-operable on their own. */}
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                            <div className="layout-sidebar" id="app-sidebar" onClick={onSidebarClick}
                                 role={mobileMenuActive ? "dialog" : undefined} aria-modal={mobileMenuActive ? true : undefined}
                                 aria-label={mobileMenuActive ? I18n.get("nav.main") : undefined}>
                                <nav aria-label={I18n.get("nav.main")}>
                                    <AppMenu onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode}/>
                                </nav>
                            </div>

                            <div className="layout-main-container">
                                <BlockUI blocked={blockedState.get()} fullScreen role="status" aria-live="polite"
                                         template={<>
                                             <i className="fcdm fcdm-m" aria-hidden="true" style={{'fontSize': '3rem'}}/>
                                             <span className="sr-only">{I18n.get("loading")}</span>
                                         </>}></BlockUI>
                                <main id="main-content" className="layout-main" tabIndex={-1}>

                                        <ScrollToTop>
                                        <Suspense fallback={
                                            <div role="status" aria-live="polite" style={{textAlign: 'center', padding: '3rem'}}>
                                                <i className="fcdm fcdm-m" aria-hidden="true" style={{fontSize: '3rem'}}/>
                                                <span className="sr-only">{I18n.get("loading")}</span>
                                            </div>
                                        }>
                                        <Routes>
                                            <Route path="/" exact element={<Home/>}/>
                                            <Route path="/profile" exact element={<Profile/>}/>
                                            <Route path="/candidate-schedule" exact element={<CandidateSchedule/>}/>
                                            <Route path="/candidate-excuses" exact element={<CandidateExcuses/>}/>
                                            <Route path="/candidate-history" exact element={<CandidateHistory/>}/>
                                            <Route path="/inscription" exact element={<Inscriptions/>}/>
                                            <Route path="/formateur-schedule" exact element={<FormateurSchedule/>}/>
                                            <Route path="/formateur-declaration-frais" exact element={<FormateurDeclarationsFrais/>}/>
                                            <Route path="/presence/:seanceId" exact element={<SaisiePresence/>}/>
                                            <Route path="/accessibilite" exact element={<Accessibilite/>}/>
                                        </Routes>
                                        </Suspense>
                                        </ScrollToTop>
                                        <CookieConsent buttonStyle={{ margin: "15px", background: "#efb600", color: "black", border: "0", boxShadow: "none", borderRadius: "0px", cursor: "pointer", flex: "0 0 auto", padding: "5px 10px" }} disableButtonStyles={true} buttonText={I18n.get("cookieaccept")}
                                        >{I18n.get("cookieconsent")}</CookieConsent>

                                </main>
                            </div>

                            <CSSTransition classNames="layout-mask" timeout={{enter: 200, exit: 200}} in={mobileMenuActive} unmountOnExit>
                                <div className="layout-mask p-component-overlay"></div>
                            </CSSTransition>
                        </UserContext.Provider>
                    </div>
                </ToastContext.Provider>
            ) : null
        )
        }/>
    );
}

export default App;
